import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import UserChartPage from "./components/UserChartPage";
import { useCachedResource } from "../hooks/useCachedResource";
import { DualMeasureDays, RankedBars, SERIES, compact, shortDay } from "./components/DashboardCharts";
import {
  FiUsers,
  FiZap,
  FiDollarSign,
  FiRefreshCw,
  FiArrowUpRight,
  FiArrowDownRight,
  FiVideo,
  FiShield,
  FiAward,
  FiCreditCard,
  FiPlay,
  FiAlertTriangle,
  FiLink,
  FiRepeat,
  FiCheckSquare,
  FiClock,
  FiChevronRight,
  FiTarget,
  FiSlash,
  FiUserCheck,
} from "react-icons/fi";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

/**
 * A number that might not be there.
 *
 * A queue whose count could not be read comes back null, and that is not the
 * same as none outstanding — a dash says "unknown", a zero would say "nothing
 * to do" and be believed.
 */
const show = (value) =>
  value === null || value === undefined ? "—" : Number(value).toLocaleString();

/**
 * A change against a named baseline.
 *
 * Direction and goodness are the same thing for every figure on this page (more
 * signups is good, more plays is good), so one component covers them all. No
 * baseline yields null rather than a fabricated 100%.
 */
const Delta = ({ value, title }) => {
  if (value === null || value === undefined) return null;
  const up = value >= 0;
  return (
    <span
      className={`text-xs font-semibold flex items-center gap-0.5 ${
        up ? "text-[#006300]" : "text-[#d03b3b]"
      }`}
      title={title}
    >
      {up ? <FiArrowUpRight size={13} /> : <FiArrowDownRight size={13} />}
      {Math.abs(value)}%
    </span>
  );
};

/**
 * The number the dashboard leads with.
 *
 * Exactly one per view. The previous design gave "453,131 total users" and
 * "3 players today" identical cards, so the page had twelve equally loud
 * numbers and therefore no answer to "how are we doing" — the reader had to
 * supply the hierarchy themselves. This is that hierarchy, made explicit.
 */
const Hero = ({ users }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-6 flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-4 sm:gap-6">
    <div>
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
        Total users
      </p>
      {/* Proportional figures, not tabular: at this size equal-width digits
          make a number look loose. Steps down on phones so a seven-digit
          count cannot push the card into a horizontal scroll. */}
      <p className="text-[2.5rem] sm:text-5xl xl:text-6xl font-bold text-gray-900 leading-none">
        {show(users.total)}
      </p>
      <div className="flex items-center gap-2 mt-2">
        <span className="text-sm text-gray-500">
          <span className="font-semibold text-gray-700">{show(users.new_today)}</span> joined today
        </span>
        <Delta value={users.change_vs_yesterday} title="Against the same point yesterday" />
      </div>
    </div>

    {/* The same measure over three windows. Reading across gives the trend
        without a chart — and the chart below gives the shape.
        A three-column grid rather than a flex row: at 375px a row of three
        wraps into a ragged two-plus-one, while the grid keeps them aligned
        and lets the labels sit under their own numbers. */}
    <div className="grid grid-cols-3 gap-3 sm:gap-8 sm:flex border-t sm:border-t-0 border-gray-100 pt-3 sm:pt-0">
      {[
        { label: "Yesterday", value: users.new_yesterday },
        { label: "Last 7 days", value: users.new_7_days },
        { label: "Last 30 days", value: users.new_30_days },
      ].map((item) => (
        <div key={item.label}>
          <p className="text-[10px] sm:text-[11px] text-gray-400 uppercase tracking-wider mb-1 leading-tight">
            {item.label}
          </p>
          <p className="text-xl sm:text-2xl font-semibold text-gray-900 leading-none">
            {show(item.value)}
          </p>
        </div>
      ))}
    </div>
  </div>
);

/**
 * One figure in the secondary strip.
 *
 * Deliberately quieter than the hero and flatter than the old cards: no icon
 * chip, no coloured left border, no shadow. Twelve cards each shouting in their
 * own hue is how the previous version lost its hierarchy — these are context,
 * and they are allowed to look like it.
 *
 * `foot` is given room to wrap rather than being truncated. The old card
 * clipped its own subtitles ("57,420 in the las…", "Started a game, …"), which
 * is the one thing a label must never do.
 */
const Stat = ({ label, value, foot, accent, icon: Icon, delta }) => (
  <div className="bg-white rounded-xl border border-gray-100 px-3 py-2.5 sm:px-3.5 sm:py-3">
    <div className="flex items-center gap-1.5 mb-1">
      {Icon && <Icon size={12} style={{ color: accent }} className="shrink-0" />}
      <p className="text-[11px] text-gray-500 font-medium leading-tight truncate">{label}</p>
    </div>
    <p className="text-xl sm:text-[1.375rem] font-semibold text-gray-900 leading-none flex items-baseline gap-1.5">
      {value}
      {delta !== undefined && <Delta value={delta} />}
    </p>
    {/* Two lines at most, then ellipsis. The old card let this wrap freely,
        which made a six-across row as tall as its wordiest member while the
        rest sat in whitespace. */}
    {foot && (
      <p className="text-[10.5px] text-gray-400 mt-1 leading-snug line-clamp-2">{foot}</p>
    )}
  </div>
);

/**
 * A queue with work in it.
 *
 * These are the numbers that turn into a task, so each one is a link to the
 * screen where the work gets done — a count you cannot act on from where it is
 * shown is just decoration. A queue at zero recedes; only work pending gets
 * the warning treatment, with an icon and a label beside it so the state never
 * rests on colour alone.
 */
const QueueCard = ({ label, count, icon: Icon, to, navigate }) => {
  const waiting = Number(count) > 0;
  return (
    <button
      onClick={() => navigate(to)}
      className={`text-left bg-white rounded-xl border px-3 py-2.5 flex items-center gap-2.5 transition hover:shadow-md ${
        waiting ? "border-[#fab219]" : "border-gray-100"
      }`}
    >
      <span
        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
          waiting ? "bg-amber-50 text-[#b07a00]" : "bg-gray-50 text-gray-300"
        }`}
      >
        <Icon size={15} />
      </span>
      <span className="min-w-0">
        <span className="block text-lg font-semibold text-gray-900 leading-tight">
          {show(count)}
        </span>
        <span className="block text-[11px] text-gray-500 leading-tight truncate">{label}</span>
      </span>
    </button>
  );
};

/**
 * Which slice of history a section is showing.
 *
 * Tasks and Games follow the running event, and fall back to all-time when
 * nothing is running. The figures look identical either way, so without this
 * a "Total completions" could be read as the wrong one.
 */
const ScopeChip = ({ scope }) =>
  scope.kind === "event" ? (
    <span
      className="inline-flex items-center gap-1 min-w-0 max-w-[220px] text-[10px] font-semibold text-[#0f7a55] bg-[#1baf7a]/10 border border-[#1baf7a]/30 rounded-full px-2 py-0.5"
      title="Everything in this section is for the running event only"
    >
      <FiPlay size={9} className="shrink-0" />
      <span className="truncate">{scope.event_name}</span>
    </span>
  ) : (
    <span
      className="text-[10px] font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5"
      title="No event is running, so these figures cover all time"
    >
      All-time
    </span>
  );

/**
 * What the per-day charts cover, in words.
 *
 * An event's series starts on its first day (or covers the last month of a
 * long one), so a fixed "Last 14 days" would be wrong either way.
 */
const seriesSubtitle = (days, scope) => {
  const count = days?.length || 0;
  if (scope.kind !== "event") return `Last ${count || 14} days, UTC`;
  if (!count) return "Each day of this event, UTC";

  const start = String(scope.start_date || "").slice(0, 10);
  if (days[0].day === start) return `Since the event began on ${shortDay(start)}, UTC`;
  return `Last ${count} day${count === 1 ? "" : "s"} of this event, UTC`;
};

/** A section heading with the link to where its work is actually done. */
const SectionHead = ({ title, subtitle, action, onAction, scope }) => (
  <div className="flex items-end justify-between gap-3 mb-2.5 mt-6">
    <div className="min-w-0">
      <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 min-w-0">
        {title}
        {scope && <ScopeChip scope={scope} />}
      </h4>
      {/* Hidden on phones: the section title already says what this is, and
          a second line of explanation costs a card's worth of height on a
          screen where vertical space is the scarce thing. */}
      {subtitle && <p className="hidden sm:block text-[11px] text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
    {action && (
      <button
        onClick={onAction}
        className="text-[11px] font-medium text-[#2a78d6] hover:text-[#1c5cab] flex items-center gap-0.5 shrink-0"
      >
        {action} <FiChevronRight size={13} />
      </button>
    )}
  </div>
);

const Card = ({ title, subtitle, children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-gray-100 p-3.5 sm:p-4 ${className}`}>
    <p className="text-xs font-semibold text-gray-800">{title}</p>
    {subtitle && <p className="text-[11px] text-gray-400 mt-0.5 mb-3 leading-snug">{subtitle}</p>}
    {children}
  </div>
);

const TASK_TYPE_LABEL = {
  website_visit: "Website Visit",
  download_app: "Download App",
  telegram: "Telegram",
  youtube: "YouTube",
};

/**
 * How long the numbers are considered current.
 *
 * Opening the dashboard inside this window costs nothing at all — no request is
 * made. Signup counts and review queues do not move meaningfully in a minute,
 * and the Refresh button is there for when they have.
 */
const KPI_TTL_MS = 60_000;

/**
 * Referrals and continuous-mining counts live behind a heavier, separate
 * endpoint (nine aggregate queries) that nothing else on this page needs.
 * A long TTL keeps it from being re-run on every dashboard visit — these two
 * numbers do not move fast enough to justify that cost.
 */
const OVERVIEW_EXTRA_TTL_MS = 300_000;

const Dashboard = () => {
  const navigate = useNavigate();

  const fetchKpis = useCallback(async (fresh) => {
    const token = localStorage.getItem("token");
    // `fresh` also skips the API's own short cache, so Refresh means refresh
    // the whole way down rather than just past the browser.
    const res = await fetch(`${baseUrl}/auth/dashboard${fresh ? "?fresh=1" : ""}`, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    });

    const result = await res.json();
    if (!res.ok || !result.status) {
      throw new Error(result.message || "Failed to fetch dashboard data");
    }
    return result.data;
  }, []);

  const fetchOverviewExtra = useCallback(async () => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${baseUrl}/analytics/overview`, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    });
    const result = await res.json();
    if (!res.ok || !result.status) {
      throw new Error(result.message || "Failed to fetch overview data");
    }
    return result.data;
  }, []);

  const { data, loading, refreshing, error, updatedAt, refresh } = useCachedResource(
    "dashboard-kpis",
    () => fetchKpis(true),
    { ttlMs: KPI_TTL_MS }
  );

  // Independent of the KPI resource above — it renders "—" until this
  // resolves rather than holding up the rest of the dashboard.
  const { data: overviewExtra } = useCachedResource(
    "dashboard-overview-extra",
    fetchOverviewExtra,
    { ttlMs: OVERVIEW_EXTRA_TTL_MS }
  );

  const users = data?.users || {};
  const mining = data?.mining || {};
  const queues = data?.queues || {};
  const events = data?.events || {};
  const tasks = data?.tasks || {};
  const games = data?.games || {};
  const owed = queues.payouts_owed || [];
  // What `tasks` and `games` were computed over. An API that predates the
  // field only ever sent all-time figures, so that is the fallback.
  const scope = data?.scope || { kind: "all_time" };
  const eventScoped = scope.kind === "event";
  const span = eventScoped ? "this event" : "all-time";

  const minerTotal = Number(mining.active || 0) + Number(mining.inactive || 0);

  return (
    <div className="flex bg-[#f9f9f7] min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        {/* `pt-16` on phones clears the sidebar's fixed hamburger, which sits
            at top-4 left-4 and would otherwise land on the page title. From
            `lg` the sidebar is docked and the button is gone, so the padding
            goes back to normal. */}
        <div className="px-3 pb-6 pt-16 sm:px-6 sm:pt-16 lg:p-8 max-w-[1600px] w-full">
          <div className="mb-4 sm:mb-5 flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard</h3>
              <p className="text-sm text-gray-400 mt-0.5">
                Welcome back, Admin
                {updatedAt && (
                  <span className="ml-2 text-gray-300">
                    · updated{" "}
                    {new Date(updatedAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                  </span>
                )}
              </p>
            </div>

            <button
              onClick={refresh}
              disabled={refreshing || loading}
              className="px-3 py-2 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 text-gray-600 text-xs font-medium rounded-xl transition shadow-sm flex items-center gap-1.5"
            >
              <FiRefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl p-6 animate-pulse h-36" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 animate-pulse h-24" />
                ))}
              </div>
            </div>
          ) : error && !data ? (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-5 mb-6">{error}</div>
          ) : (
            // A refetch holds the previous render at reduced opacity rather
            // than flashing a skeleton — no layout jump, numbers stay readable.
            <div className={refreshing ? "opacity-60 transition-opacity" : "transition-opacity"}>
              {/* A refresh that failed over numbers that are already on screen:
                  say so, but do not replace them with an error. */}
              {error && (
                <div className="mb-4 px-4 py-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-xs">
                  Showing the last known figures — {error}
                </div>
              )}

              {/* Anything needing an admin, first — a dashboard should open on
                  the work, not on how big the product is. */}
              {(queues.pending_payouts > 0 || events.needs_finalizing > 0) && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-amber-900">
                  <span className="font-bold flex items-center gap-1.5">
                    <FiAlertTriangle size={14} /> Needs attention
                  </span>
                  {events.needs_finalizing > 0 && (
                    <button onClick={() => navigate("/event?status=running")} className="underline">
                      {events.needs_finalizing} event
                      {events.needs_finalizing === 1 ? "" : "s"} past their end date, not finalized
                    </button>
                  )}
                  {queues.pending_payouts > 0 && (
                    <button onClick={() => navigate("/reward-coin-approval")} className="underline">
                      {queues.pending_payouts} reward
                      {queues.pending_payouts === 1 ? "" : "s"} awaiting approval
                      {owed.length > 0 && (
                        <span className="not-underline">
                          {" "}
                          ({owed.map((o) => `${o.amount.toLocaleString()} ${o.currency}`).join(" · ")})
                        </span>
                      )}
                    </button>
                  )}
                </div>
              )}

              <Hero users={users} />

              {/* The running event, if there is one. */}
              {events.running && (
                <button
                  onClick={() => navigate(`/event/${events.running.id}`)}
                  className="w-full text-left mt-4 p-4 bg-white rounded-xl border border-[#1baf7a]/30 hover:shadow-md transition flex items-center gap-4"
                >
                  <span className="w-11 h-11 rounded-xl bg-[#1baf7a]/10 text-[#0f7a55] flex items-center justify-center shrink-0">
                    <FiPlay size={18} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      Running now
                    </span>
                    <span className="block text-sm font-semibold text-gray-900 truncate">
                      {events.running.name}
                    </span>
                    <span className="block text-[11px] text-gray-500">
                      {show(events.running.participants)} participants
                    </span>
                  </span>
                </button>
              )}

              {/* Platform context. Quieter than the hero on purpose. */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-3 mt-3">
                <Stat
                  label="Active miners"
                  value={show(mining.active)}
                  foot={
                    minerTotal > 0
                      ? `${Math.round((mining.active / minerTotal) * 100)}% of all users`
                      : null
                  }
                  icon={FiZap}
                  accent={SERIES.points}
                />
                <Stat
                  label="Continuous miners"
                  value={show(overviewExtra?.continuousUsers)}
                  foot="Mined in the last 7 days"
                  icon={FiRepeat}
                  accent={SERIES.people}
                />
                <Stat
                  label="Total coins mined"
                  value={compact(mining.total_coins)}
                  foot={Number(mining.total_coins || 0).toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })}
                  icon={FiDollarSign}
                  accent={SERIES.count}
                />
                <Stat
                  label="Total referrals"
                  value={show(overviewExtra?.totalReferrals)}
                  foot="All-time"
                  icon={FiLink}
                  accent={SERIES.count}
                />
                <Stat
                  label="Inactive miners"
                  value={show(mining.inactive)}
                  foot="Not currently mining"
                  icon={FiUsers}
                  accent="#898781"
                />
              </div>

              {/* ── Waiting for review ─────────────────────────────────── */}
              <SectionHead
                title="Waiting for review"
                subtitle="Queues that turn into work — each opens where it gets done"
              />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
                <QueueCard
                  label="Creator submissions"
                  count={queues.creator_hub}
                  icon={FiVideo}
                  to="/creator-hub"
                  navigate={navigate}
                />
                <QueueCard
                  label="Profile verifications"
                  count={queues.profile_verification}
                  icon={FiShield}
                  to="/profile-verifications"
                  navigate={navigate}
                />
                <QueueCard
                  label="Pioneer requests"
                  count={queues.pioneer_requests}
                  icon={FiAward}
                  to="/pioneer-list"
                  navigate={navigate}
                />
                <QueueCard
                  label="Reward approvals"
                  count={queues.pending_payouts}
                  icon={FiCreditCard}
                  to="/reward-coin-approval"
                  navigate={navigate}
                />
              </div>

              {/* ── Tasks ─────────────────────────────────────────────── */}
              <SectionHead
                title="Tasks"
                subtitle="Completions and the event points they pay out"
                action="Manage tasks"
                onAction={() => navigate("/tasks")}
                scope={scope}
              />

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-3">
                <Stat
                  label="Completed today"
                  value={show(tasks.completions_today)}
                  foot={`${show(tasks.completions_7_days)} in the last 7 days`}
                  icon={FiCheckSquare}
                  accent={SERIES.count}
                />
                <Stat
                  label="Points today"
                  value={show(tasks.points_today)}
                  foot={`${show(tasks.points_7_days)} in the last 7 days`}
                  icon={FiTarget}
                  accent={SERIES.points}
                />
                <Stat
                  label="Total completions"
                  value={show(tasks.total_completions)}
                  foot={`by ${show(tasks.participants)} user${tasks.participants === 1 ? "" : "s"}`}
                  icon={FiAward}
                  accent={SERIES.count}
                />
                <Stat
                  label="Total task points"
                  value={show(tasks.total_points)}
                  foot={
                    tasks.avg_points_per_completion === null ||
                    tasks.avg_points_per_completion === undefined
                      ? `Paid out ${span}`
                      : `${tasks.avg_points_per_completion} avg per completion`
                  }
                  icon={FiTarget}
                  accent={SERIES.points}
                />
                <Stat
                  label="Opened, not finished"
                  value={show(tasks.started_not_completed)}
                  foot="Visited a task and dropped off"
                  icon={FiClock}
                  accent="#898781"
                />
                <Stat
                  label="Active tasks"
                  value={show(tasks.active_tasks)}
                  // With no event running the app shows no tasks at all, so
                  // "live" would only be true of the event-scoped count.
                  foot={eventScoped ? "Live in the app right now" : "Switched on, across all events"}
                  icon={FiZap}
                  accent={SERIES.people}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
                <Card title="Completions and points per day" subtitle={seriesSubtitle(tasks.by_day, scope)}>
                  {tasks.by_day && tasks.by_day.length > 0 ? (
                    <DualMeasureDays
                      days={tasks.by_day}
                      countKey="completions"
                      countLabel="Completions"
                      countUnit="completions"
                      accent={SERIES.count}
                    />
                  ) : (
                    <p className="py-10 text-center text-xs text-gray-400">No completions yet.</p>
                  )}
                </Card>

                <Card
                  title="Most completed"
                  subtitle="One completion per user, so this is also how many people finished each"
                >
                  <RankedBars
                    accent={SERIES.count}
                    emptyText="Nobody has completed a task yet."
                    rows={(tasks.top_tasks || []).map((task) => ({
                      key: task.id,
                      label: task.title || `#${task.id}`,
                      value: task.completions,
                      muted: task.is_active ? null : "off",
                      foot: `${TASK_TYPE_LABEL[task.task_type] || task.task_type} · ${show(
                        task.points
                      )} pts`,
                    }))}
                  />
                </Card>
              </div>

              {tasks.by_type && tasks.by_type.length > 0 && (
                <Card
                  title="By task type"
                  subtitle={`${eventScoped ? "Completions in this event" : "All-time completions"} and the points they paid out`}
                  className="mt-3"
                >
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
                    {tasks.by_type.map((row) => (
                      <div key={row.task_type} className="rounded-lg border border-gray-100 px-2.5 py-2">
                        <p className="text-[11px] font-medium text-gray-600 truncate">
                          {TASK_TYPE_LABEL[row.task_type] || row.task_type}
                        </p>
                        <p className="text-xl font-semibold text-gray-900 mt-1">
                          {show(row.completions)}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-0.5 leading-snug">
                          {show(row.users)} user{row.users === 1 ? "" : "s"} ·{" "}
                          <span style={{ color: SERIES.points }} className="font-medium">
                            {show(row.points)} pts
                          </span>
                        </p>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* ── Games ─────────────────────────────────────────────── */}
              <SectionHead
                title="Games"
                subtitle="Plays, the people behind them, and the points paid out"
                action="Manage games"
                onAction={() => navigate("/games")}
                scope={scope}
              />

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-3">
                <Stat
                  label="Plays today"
                  value={show(games.plays_today)}
                  foot={`${show(games.plays_7_days)} in the last 7 days`}
                  icon={FiPlay}
                  accent={SERIES.count}
                />
                <Stat
                  label="Points today"
                  value={show(games.points_today)}
                  foot={`${show(games.points_7_days)} in the last 7 days`}
                  icon={FiTarget}
                  accent={SERIES.points}
                />
                <Stat
                  label="Players today"
                  value={show(games.players_today)}
                  foot={`${show(games.players)} ${span}`}
                  icon={FiUserCheck}
                  accent={SERIES.people}
                />
                <Stat
                  label="Total game points"
                  value={show(games.total_points)}
                  foot={
                    games.avg_points_per_play === null || games.avg_points_per_play === undefined
                      ? `Paid out ${span}`
                      : `${games.avg_points_per_play} avg per play`
                  }
                  icon={FiTarget}
                  accent={SERIES.points}
                />
                <Stat
                  label="Abandoned"
                  value={show(games.abandoned)}
                  foot="Started a game, never finished"
                  icon={FiClock}
                  accent="#898781"
                />
                <Stat
                  label="Disqualified"
                  value={show(games.disqualified)}
                  foot="Sessions rejected by anti-cheat"
                  icon={FiSlash}
                  accent="#d03b3b"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
                <Card title="Plays and points per day" subtitle={seriesSubtitle(games.by_day, scope)}>
                  {games.by_day && games.by_day.length > 0 ? (
                    <DualMeasureDays
                      days={games.by_day}
                      countKey="plays"
                      countLabel="Plays"
                      countUnit="plays"
                      accent={SERIES.count}
                    />
                  ) : (
                    <p className="py-10 text-center text-xs text-gray-400">Nobody has played yet.</p>
                  )}
                </Card>

                <Card
                  title="By game"
                  subtitle={`${eventScoped ? "Plays in this event" : "All-time plays"}, the people behind them, and what each has paid out`}
                >
                  <RankedBars
                    accent={SERIES.count}
                    emptyText="No game sessions recorded yet."
                    rows={(games.by_game || []).map((game) => ({
                      key: game.game_key,
                      label: game.name || game.game_key,
                      value: game.plays,
                      // A retired game keeps its history but no longer exists in
                      // the catalog, which is a different thing from switched off.
                      muted: game.retired ? "retired" : game.is_active === false ? "off" : null,
                      foot: (
                        <>
                          {show(game.players)} player{game.players === 1 ? "" : "s"} ·{" "}
                          <span style={{ color: SERIES.points }} className="font-medium">
                            {show(game.points)} pts
                          </span>{" "}
                          · {game.avg_points} avg
                          {game.disqualified > 0 && (
                            <span style={{ color: "#d03b3b" }}> · {game.disqualified} DQ</span>
                          )}
                        </>
                      ),
                    }))}
                  />
                </Card>
              </div>

            </div>
          )}

          {/* Outside the KPI branch: user growth has its own endpoint and its
              own cache, so a failed or slow KPI fetch must not blank it. */}
          <div className="mt-8">
            <UserChartPage />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
